/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";
import axios from "axios";
import { API_ESA_OFFICE } from "utils";

// formik components
import { Formik, Form } from "formik";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// NewUser page components
import AkunRSS from "layouts/pages/users/new-user/components/AkunRSS";
import InfoUser from "layouts/pages/users/new-user/components/InfoUser";
import Socials from "layouts/pages/users/new-user/components/Socials";
import Profile from "layouts/pages/users/new-user/components/Profile";

// NewUser layout schemas for form and form feilds
import validations from "layouts/pages/users/new-user/schemas/validations";
import form from "layouts/pages/users/new-user/schemas/form";
import initialValues from "layouts/pages/users/new-user/schemas/initialValues";
import FIREBASE from "../../../../config/FIREBASE";
import swal from "sweetalert";
import { logoutUser } from "actions/AuthAction";
import { useNavigate } from "react-router-dom";

function getSteps() {
  return ["Akun RSS", "Info User", "Social", "Profile"];
}

function getStepContent(stepIndex, formData) {
  switch (stepIndex) {
    case 0:
      return <AkunRSS formData={formData} />;
    case 1:
      return <InfoUser formData={formData} />;
    case 2:
      return <Socials formData={formData} />;
    case 3:
      return <Profile formData={formData} />;
    default:
      return null;
  }
}

function convertGambar(avatar) {
  if (avatar) {
    const imageDataURI = avatar;
    const [dataType, base64Data] = imageDataURI.split(",");
    const binaryData = atob(base64Data);
    const len = binaryData.length;
    const uint8Array = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      uint8Array[i] = binaryData.charCodeAt(i);
    }
    // Ambil mime type yang benar
    const mime = dataType.match(/:(.*?);/)[1];
    const file = new File([uint8Array], "avatar.jpg", { type: mime });
    return file;
  } else {
    return "";
  }
}

function NewUser() {
  const [activeStep, setActiveStep] = useState(0);
  const [hasilCari, setHasilCari] = useState(false);
  const [loadingCari, setLoadingCari] = useState(false);
  const [userDitemukan, setUserDitemukan] = useState(null);

  const [uid_RSS, setUidRSS] = useState(null);
  const [dinasId, setDinasId] = useState(null);
  const navigate = useNavigate();
  const steps = getSteps();
  const { formId, formField } = form;
  const currentValidation = validations[activeStep];
  const isLastStep = activeStep === steps.length - 1;

  const sleep = (ms) =>
    new Promise((resolve) => {
      setTimeout(resolve, ms);
    });
  const handleBack = () => setActiveStep(activeStep - 1);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      setDinasId(user.dinas_id);
    } else {
      logoutUser(navigate);
    }
  }, []);

  const submitForm = async (values, actions) => {
    await sleep(1000);

    // Konversi avatar base64 ke File jika perlu (pakai convertGambar)
    if (
      values.avatar &&
      typeof values.avatar === "string" &&
      values.avatar.startsWith("data:image")
    ) {
      values.avatar = convertGambar(values.avatar);
    }

    // Cek jika ada file gambar (avatar) yang berupa File, gunakan FormData
    let isFile =
      values.avatar &&
      typeof values.avatar !== "string" &&
      values.avatar instanceof File;
    try {
      let response;
      if (isFile) {
        const formData = new FormData();
        Object.keys(values).forEach((key) => {
          if (key === "avatar" && values.avatar) {
            formData.append("avatar", "values.avatar");
          } else {
            formData.append(key, values[key]);
          }
        });
        response = await axios.post(`${API_ESA_OFFICE}users`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        response = await axios.post(`${API_ESA_OFFICE}users`, values);
      }
      swal("Sukses", "Data user berhasil dikirim!", "success");
      actions.setSubmitting(false);
      setUidRSS(null);
      setUserDitemukan(null);
      setHasilCari(false);

      actions.resetForm();
      setActiveStep(0);
    } catch (err) {
      swal(
        "Gagal",
        err?.response?.data?.error || "Gagal mengirim data!",
        "error"
      );
      actions.setSubmitting(false);
    }
  };

  const handleSubmit = (values, actions) => {
    if (isLastStep) {
      submitForm(values, actions);
    } else {
      setActiveStep(activeStep + 1);
      actions.setTouched({});
      actions.setSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3} mb={20} height="65vh">
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          sx={{ height: "100%", mt: 8 }}
        >
          <Grid item xs={12} lg={8}>
            <Formik
              initialValues={initialValues}
              validationSchema={currentValidation}
              onSubmit={handleSubmit}
            >
              {({ values, errors, touched, isSubmitting, setValues }) => (
                <Form id={formId} autoComplete="off">
                  <Card sx={{ height: "100%" }}>
                    <MDBox
                      mx={2}
                      mt={2}
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <MDBox flex={1}>
                        <Stepper activeStep={activeStep} alternativeLabel>
                          {steps.map((label) => (
                            <Step key={label}>
                              <StepLabel>{label}</StepLabel>
                            </Step>
                          ))}
                        </Stepper>
                      </MDBox>
                      {userDitemukan && userDitemukan.avatar && (
                        <MDBox
                          display="flex"
                          flexDirection="column"
                          alignItems="center"
                          ml={3}
                        >
                          <MDBox
                            component="img"
                            src={userDitemukan.avatar}
                            alt="Profile Picture"
                            sx={{
                              width: 64,
                              height: 64,
                              borderRadius: "50%",
                              objectFit: "cover",
                              boxShadow: 3,
                              border: "3px solid #fff",
                            }}
                          />
                        </MDBox>
                      )}
                    </MDBox>
                    <MDBox p={3}>
                      <MDBox>
                        {getStepContent(activeStep, {
                          values,
                          touched,
                          formField,
                          errors,
                          setValues,
                        })}
                        <MDBox
                          mt={2}
                          width="100%"
                          display="flex"
                          justifyContent="space-between"
                        >
                          {activeStep === 0 ? (
                            <MDBox />
                          ) : (
                            <MDButton
                              variant="gradient"
                              color="light"
                              onClick={handleBack}
                            >
                              back
                            </MDButton>
                          )}
                          {(activeStep > 0 ||
                            (activeStep === 0 && hasilCari)) && (
                            <MDButton
                              disabled={isSubmitting}
                              type="submit"
                              variant="gradient"
                              color="dark"
                            >
                              {isLastStep ? "send" : "next"}
                            </MDButton>
                          )}
                          {activeStep === 0 && !hasilCari && (
                            <MDButton
                              type="button"
                              variant="gradient"
                              color="dark"
                              disabled={loadingCari}
                              onClick={async () => {
                                if (!values.email || !values.email.trim()) {
                                  swal("Email tidak boleh kosong", "", "error");
                                  return;
                                }
                                let instansiKu = "";
                                const userStorage = localStorage.getItem("user")
                                  ? JSON.parse(localStorage.getItem("user"))
                                  : null;
                                if (userStorage && userStorage.nama_dinas) {
                                  instansiKu = userStorage.nama_dinas;
                                }

                                setLoadingCari(true);
                                setHasilCari(false);
                                setUserDitemukan(null);
                                setUidRSS(null);
                                const email = values.email.trim();
                                let found = false;
                                let userData = null;
                                try {
                                  const snapshot = await FIREBASE.database()
                                    .ref("users")
                                    .orderByChild("email")
                                    .equalTo(email)
                                    .once("value");
                                  found = snapshot.exists();
                                  if (found) {
                                    // Ambil data user pertama dari hasil snapshot
                                    const val = snapshot.val();
                                    const keys = Object.keys(val);
                                    if (keys.length > 0) {
                                      userData = val[keys[0]];
                                      setUserDitemukan(userData);
                                      setUidRSS(userData.uid);
                                    }
                                  }
                                } catch (e) {
                                  found = false;
                                  userData = null;
                                }
                                setHasilCari(found);

                                setLoadingCari(false);
                                if (!found) {
                                  swal(
                                    "Username / Email " +
                                      values.email +
                                      " tidak ditemukan",
                                    "",
                                    "error"
                                  );
                                } else {
                                  swal(
                                    "Username / Email " +
                                      values.email +
                                      " ditemukan",
                                    "",
                                    "success"
                                  );
                                  setValues({
                                    ...values,
                                    nama: userData.nama || "",
                                    uid_RSS: userData.uid || "",
                                    avatar: userData.avatar || "",
                                    instansi: instansiKu,
                                  });
                                }
                              }}
                            >
                              {loadingCari ? "Mencari..." : "Cari"}
                            </MDButton>
                          )}
                        </MDBox>
                      </MDBox>
                    </MDBox>
                  </Card>
                </Form>
              )}
            </Formik>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default NewUser;
